import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setUser, clearUser } from '../state/index';
import { 
  TextField, 
  Button, 
  Typography, 
  Box, 
  Alert, 
  Paper,
  FormControlLabel,
  Checkbox,
  Avatar,
  Stack,
  Tooltip,
} from '@mui/material';
import { getTheme } from '../theme/modernTheme';
import BrandLogo from './BrandLogo';

/** Safari vb. yerel "Mail hatası" balonunu engeller (HTML5 constraint); doğrulama handleSubmit içinde */
const suppressBrowserFieldValidation = {
  onInvalid: (e) => {
    e.preventDefault();
  },
  onInput: (e) => {
    if (typeof e.target?.setCustomValidity === 'function') {
      e.target.setCustomValidity('');
    }
  },
};

const LOGIN_PROFILES_KEY = 'crm_saved_login_profiles';

function loadSavedProfiles() {
  try {
    const raw = localStorage.getItem(LOGIN_PROFILES_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function upsertSavedProfile(user) {
  if (!user?.email) return;
  const entry = {
    id: String(user.id ?? user._id ?? ''),
    email: user.email,
    name: user.name || '',
    profileImage: user.profileImage || null,
  };
  let list = loadSavedProfiles().filter((p) => p.email !== entry.email);
  list.unshift(entry);
  list = list.slice(0, 5);
  localStorage.setItem(LOGIN_PROFILES_KEY, JSON.stringify(list));
}

function normalizeCurrentUser(raw) {
  if (!raw) return null;
  const id = raw._id ?? raw.id;
  if (!id) return null;
  return {
    id: String(id),
    name: raw.name,
    email: raw.email,
    role: raw.role,
    occupation: raw.occupation,
    profileImage: raw.profileImage,
    city: raw.city,
    state: raw.state,
    country: raw.country,
  };
}

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(() =>
    localStorage.getItem('crm_prefer_remember_login') !== '0'
  );
  const [savedProfiles, setSavedProfiles] = useState(() => loadSavedProfiles());
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const mode = useSelector((state) => state.global.mode);
  const currentTheme = getTheme(mode);

  const { reason, nextPath } = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    const next = sp.get('next');
    let decoded = '';
    try { decoded = next ? decodeURIComponent(next) : ''; } catch { decoded = ''; }
    if (decoded && !decoded.startsWith('/')) decoded = '';
    return { reason: sp.get('reason') || '', nextPath: decoded };
  }, [location.search]);

  const sessionInfo = useMemo(() => {
    if (reason === 'expired') return 'Oturumunuzun süresi doldu, lütfen yeniden giriş yapın.';
    if (reason === 'token_invalid' || reason === 'auth_error') return 'Oturum doğrulanamadı, lütfen tekrar giriş yapın.';
    if (reason === 'user_missing') return 'Hesabınız bulunamadı, lütfen tekrar giriş yapın.';
    return '';
  }, [reason]);

  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token');
    if (!token) return undefined;

    (async () => {
      try {
        const { data } = await axios.get('/api/general/user/current');
        if (cancelled) return;
        const client = normalizeCurrentUser(data?.user);
        if (client?.id) {
          localStorage.setItem('user', JSON.stringify(client));
          dispatch(setUser(client.id));
          navigate(nextPath || '/dashboard', { replace: true });
        }
      } catch {
        // Geçersiz token: axiosAuth 401'de temizler; formda kalınır
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch, navigate, nextPath]);

  const pickSavedProfile = async (p) => {
    const targetEmail = (p?.email || '').trim().toLowerCase();
    setEmail(p?.email || '');
    setPassword('');
    setError('');
    const token = localStorage.getItem('token');
    if (!token || !targetEmail) {
      setTimeout(() => document.getElementById('login-password')?.focus(), 0);
      return;
    }
    try {
      const { data } = await axios.get('/api/general/user/current');
      const u = normalizeCurrentUser(data?.user);
      if (u && u.email?.toLowerCase() === targetEmail) {
        localStorage.setItem('user', JSON.stringify(u));
        dispatch(setUser(u.id));
        navigate(nextPath || '/dashboard', { replace: true });
        return;
      }
    } catch {}
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('userId');
      dispatch(clearUser());
    } catch {}
    setTimeout(() => document.getElementById('login-password')?.focus(), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const trimmedEmail = email.trim();
    const trimmedPassword = password;
    // E-posta doğrulaması yalnızca burada (Safari yerel "Mail hatası" için type=text + onInvalid engeli)
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
    if (!trimmedEmail || !trimmedPassword) {
      setError('E-posta ve şifre gereklidir.');
      setLoading(false);
      return;
    }
    if (!emailOk) {
      setError('Geçerli bir e-posta adresi girin.');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post('/api/auth/login', {
        email: trimmedEmail,
        password: trimmedPassword,
        rememberMe,
      }, {
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.data?.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        upsertSavedProfile(response.data.user);
        setSavedProfiles(loadSavedProfiles());
        dispatch(setUser(response.data.user.id));
        navigate(nextPath || '/dashboard', { replace: true });
      } else {
        setError('Giriş başarısız. Lütfen tekrar deneyin.');
      }
    } catch (err) {
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Giriş başarısız. Lütfen e-posta ve şifrenizi kontrol edin.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: currentTheme.colors.gradient.background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2
      }}
    >
      <Paper
        elevation={24}
        sx={{
          padding: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          maxWidth: 400,
          width: '100%',
          background: currentTheme.colors.surface.primary,
          backdropFilter: currentTheme.effects.backdrop,
          borderRadius: 3,
          border: `1px solid ${currentTheme.colors.border.primary}`,
          boxShadow: currentTheme.shadows.glass
        }}
      >
        <BrandLogo
          size={100}
          sx={{
            mb: 2.5,
            maxWidth: 280,
            mx: "auto",
            objectPosition: "center",
          }}
        />

        <Typography
          component="h1"
          variant="subtitle1"
          sx={{
            mb: 3,
            fontWeight: 600,
            color: currentTheme.colors.text.secondary,
            textAlign: "center",
          }}
        >
          Proje yönetim sistemi
        </Typography>

        {sessionInfo && (
          <Alert
            severity="info"
            variant="outlined"
            sx={{
              width: '100%',
              mb: 1.5,
              borderRadius: 2,
              borderColor: currentTheme.colors.border.primary,
              color: currentTheme.colors.text.primary,
            }}
          >
            {sessionInfo}
          </Alert>
        )}

        {savedProfiles.length > 0 && (
          <Box sx={{ width: '100%', mb: 2 }}>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mb: 1,
                color: currentTheme.colors.text.muted,
                textAlign: 'center',
              }}
            >
              Bu cihazda kayıtlı hesaplar
            </Typography>
            <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" useFlexGap>
              {savedProfiles.map((p) => (
                <Tooltip key={p.email} title={p.name || p.email}>
                  <Avatar
                    alt={p.name || ''}
                    src={p.profileImage || undefined}
                    onClick={() => pickSavedProfile(p)}
                    sx={{
                      width: 48,
                      height: 48,
                      cursor: 'pointer',
                      border: `2px solid ${currentTheme.colors.border.primary}`,
                      '&:hover': { borderColor: currentTheme.colors.primary, opacity: 0.95 },
                    }}
                  >
                    {(p.name || p.email || '?').charAt(0).toUpperCase()}
                  </Avatar>
                </Tooltip>
              ))}
            </Stack>
          </Box>
        )}

        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          autoComplete="off"
          sx={{ width: '100%' }}
        >
          <TextField
            label="E-posta"
            type="text"
            name="email"
            id="login-email"
            autoComplete="off"
            inputMode="text"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            fullWidth
            margin="normal"
            inputProps={{
              ...suppressBrowserFieldValidation,
              autoCapitalize: 'none',
              autoCorrect: 'off',
              spellCheck: false,
              'data-lpignore': 'true',
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                '&:hover fieldset': {
                  borderColor: currentTheme.colors.primary,
                },
                '&.Mui-focused fieldset': {
                  borderColor: currentTheme.colors.primary,
                },
              },
              '& .MuiInputLabel-root': {
                color: currentTheme.colors.text.muted,
                '&.Mui-focused': {
                  color: currentTheme.colors.primary,
                },
              },
              '& .MuiInputBase-input': {
                color: currentTheme.colors.text.primary,
              },
            }}
          />
          <TextField
            label="Şifre"
            type="password"
            name="password"
            autoComplete="current-password"
            id="login-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            margin="normal"
            inputProps={suppressBrowserFieldValidation}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                '&:hover fieldset': {
                  borderColor: currentTheme.colors.primary,
                },
                '&.Mui-focused fieldset': {
                  borderColor: currentTheme.colors.primary,
                },
              },
              '& .MuiInputLabel-root': {
                color: currentTheme.colors.text.muted,
                '&.Mui-focused': {
                  color: currentTheme.colors.primary,
                },
              },
              '& .MuiInputBase-input': {
                color: currentTheme.colors.text.primary,
              },
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={rememberMe}
                onChange={(e) => {
                  const v = e.target.checked;
                  setRememberMe(v);
                  localStorage.setItem('crm_prefer_remember_login', v ? '1' : '0');
                }}
                sx={{
                  color: currentTheme.colors.text.muted,
                  '&.Mui-checked': { color: currentTheme.colors.primary },
                }}
              />
            }
            label="Beni hatırla (uzun oturum)"
            sx={{
              mt: 0.5,
              alignSelf: 'flex-start',
              '& .MuiFormControlLabel-label': {
                fontSize: '0.875rem',
                color: currentTheme.colors.text.secondary,
              },
            }}
          />
          <Button 
            type="submit" 
            variant="contained" 
            fullWidth
            disabled={loading}
            sx={{ 
              mt: 2, 
              mb: 2,
              height: 48,
              borderRadius: 2,
              background: mode === 'dark' ? '#fafafa' : '#0a0a0a',
              color: mode === 'dark' ? '#0a0a0a' : '#fafafa',
              fontWeight: 600,
              fontSize: '1rem',
              '&:hover': {
                background: mode === 'dark' ? '#e5e5e5' : '#262626',
                transform: 'translateY(-1px)',
                boxShadow: currentTheme.shadows.md
              },
              '&:disabled': {
                background: currentTheme.colors.surface.glass,
                color: currentTheme.colors.text.muted
              }
            }}
          >
            {loading ? 'Giriş Yapılıyor...' : 'Giriş Yap'}
          </Button>
          
          {error && (
            <Alert 
              severity="error" 
              variant="filled"
              sx={{ 
                mt: 2,
                borderRadius: 2,
                bgcolor: '#ef4444',
                color: '#fff',
                '& .MuiAlert-icon': { color: '#fff' }
              }}
            >
              {error}
            </Alert>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default Login;
